import { Component, Inject, OnInit, OnDestroy } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Observable, Subject } from "rxjs";
import { takeUntil, filter, switchMap, take } from "rxjs/operators";
import {
  ReloadableCardGlobalConfig,
  ReloadableCardsGlobalsService,
} from "../../../../../services/reloadable-cards-globals.service";
import { ReloadableCard } from "../../../../../../app/users-api";

export interface ReloadableCardModalData {
  mode: "add" | "edit";
  card?: ReloadableCard;
}

@Component({
  selector: "app-reloadable-card-modal",
  templateUrl: "./reloadable-card-modal.component.html",
  styleUrls: ["./reloadable-card-modal.component.scss"],
})
export class ReloadableCardModalComponent implements OnInit, OnDestroy {
  cardForm: FormGroup;
  configurableCards$: Observable<ReloadableCardGlobalConfig[]>;
  selectedCard: ReloadableCardGlobalConfig | undefined;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<ReloadableCardModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReloadableCardModalData,
    private reloadableCardsGlobalsService: ReloadableCardsGlobalsService
  ) {
    this.cardForm = this.fb.group({
      tarjeta: [null, Validators.required],
      transfer_min: [0.01, [Validators.required, Validators.min(0.01)]],
      transfer_max: [null],
      price_factor: [0, [Validators.required, Validators.min(0)]],
      price_offset: [0, [Validators.required, Validators.min(0)]],
    });

    if (data.mode === 'edit' && data.card) {
      this.cardForm.patchValue({
        transfer_min: data.card.transfer_min_cents / 100,
        transfer_max: data.card.transfer_max_cents / 100,
        price_factor: data.card.price_factor,
        price_offset: data.card.price_offset_cents / 100,
      });
    }
  }

  ngOnInit(): void {
    this.configurableCards$ =
      this.reloadableCardsGlobalsService.getConfigurableReloadableCards();

    if (this.data.mode === 'edit' && this.data.card) {
      this.configurableCards$.pipe(
        filter(cards => !!cards && cards.length > 0),
        take(1),
        takeUntil(this.unsubscribe$)
      ).subscribe(() => {
        this.cardForm.get('tarjeta')?.setValue(this.data.card?.card_id);
      });
    }

    this.cardForm.get('tarjeta')?.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      filter(cardId => !!cardId),
      switchMap(cardId => this.configurableCards$.pipe(
        filter(cards => !!cards),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        switchMap(cards => [cards.find(card => card.id === cardId)!])
      ))
    ).subscribe(card => {
      this.selectedCard = card;
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onSave(): void {
    if (this.cardForm.valid) {
      const formValue = this.cardForm.value;
      const card: ReloadableCard = {
        card_id: formValue.tarjeta,
        transfer_min_cents: Math.round(formValue.transfer_min * 100),
        price_factor: formValue.price_factor,
        price_offset_cents: Math.round(formValue.price_offset * 100),
      };
      if (formValue.transfer_max) {
        card.transfer_max_cents = Math.round(formValue.transfer_max * 100);
      }
      this.dialogRef.close(card);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
